/*
 * IMPORTANT: This must be the first import to set __webpack_public_path__
 * Before any other modules that might load assets (CSS, images, fonts)
 */
import '../public_path';

import 'app/interfaces/WindowInterface';
import 'lazysizes';
import '../polyfill';
import 'moment';
import 'moment-timezone';
import 'bootstrap';
import 'perfect-scrollbar.min';
import 'slick.min';
import 'resizable';
import {
    artifactStore,
    buyOrderStore,
    cashoutsStore,
    p2pDepositsStore,
    expressBetStore,
    giveawayStore,
    notificationSettingsStore,
    orderStore,
    pageContentStore,
    promoActionStore,
    quickBetStore,
    recipeStore,
    screenStore,
    sellOrderStore,
    shopLootboxStore,
    otherItemStore,
    siteNotificationStore,
    smileStore,
    steamTradeStore,
    storiesStore,
    streamStore,
    tableStore,
    userBetsStore,
    userStore,
    casinoGamesStore,
    userCasinoLogStore,
    articlesStore,
    chatStore,
    activeSurveyStore,
    shopStore,
    betSlipStore,
    artifactBetStore,
    incubatorStore,
    gamesStore,
    incubatorFilterStore,
    betsFilterStore,
    resultsFilterStore,
    incubatorShiftStore,
    incubatorUserLinesStore,
    userBonusesStore,
    modalsStore,
    faqStore,
    configStore
} from 'app/store/global';
import {isDesktopMedia, isGuest, userSignedIn} from 'app/utils';
import React, {Fragment} from 'react';
import ReactDOM from 'react-dom/client';
import {init} from 'app/common';
import 'app/firebase';
import {configure, reaction} from 'mobx';
import {Provider} from 'mobx-react';
import PwaInstallNotification from 'app/components/informers/pwa_install_notification';
import {currentTimeStore} from 'stores/global';
import NotificationStore from 'app/store/notifications';
import {amplitudeInit} from 'app/amplitude/amplitude';
import {getLanguageStorage, setLanguage} from 'app/utils/Language';
import {RouterProvider} from 'react-router-dom';
import DepositsStore from 'app/store/DepositsStore';
import {router} from 'app/components/routes/router';
import {getRecaptchaV3SiteKey} from 'app/utils/recaptchaConfig';
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3';
import {InitialDataService} from 'app/services/InitialDataService';
import MaintenanceStub from 'app/components/common/MaintenanceStub';
import {MaintenanceModeService} from 'app/services/MaintenanceModeService';

configure({enforceActions: 'always'});

currentTimeStore.setTime(userStore.user.time_zone.t);
chatStore.init();
screenStore.setDesktop(isDesktopMedia());

modalsStore.init([]);

InitialDataService.load(
    streamStore, storiesStore, recipeStore, faqStore, gamesStore, giveawayStore, promoActionStore, siteNotificationStore, activeSurveyStore
);

const stores = {
    NotificationStore,
    activeSurveyStore,
    articlesStore,
    artifactBetStore,
    artifactStore,
    betSlipStore,
    betsFilterStore,
    buyOrderStore,
    cashoutsStore,
    casinoGamesStore,
    chatStore,
    currentTimeStore,
    depositsStore: new DepositsStore(),
    expressBetStore,
    faqStore,
    gamesStore,
    giveawayStore,
    incubatorFilterStore,
    incubatorShiftStore,
    incubatorStore,
    incubatorUserLinesStore,
    modalsStore,
    notificationSettingsStore,
    orderStore,
    otherItemStore,
    p2pDepositsStore,
    pageContentStore,
    promoActionStore,
    quickBetStore,
    recipeStore,
    resultsFilterStore,
    screenStore,
    sellOrderStore,
    shopLootboxStore,
    shopStore,
    siteNotificationStore,
    smileStore,
    steamTradeStore,
    storiesStore,
    streamStore,
    tableStore,
    userBetsStore,
    userBonusesStore,
    userCasinoLogStore,
    userStore
};

document.addEventListener('DOMContentLoaded', () => {
    const rootEl = document.getElementById('app');

    if (!rootEl) {
        console.error('app root element not found');
        return;
    }
    let dispose = null;

    dispose = reaction(
        () => configStore.loaded,
        loaded => {
            if (!loaded) {
                return;
            }

            renderApp(rootEl);

            if (typeof dispose === 'function') {
                dispose();
            }
        },
        {fireImmediately: true}
    );
});

let appRoot = null;

const getAppRoot = rootEl => {
    if (!appRoot) {
        appRoot = ReactDOM.createRoot(rootEl);
    }

    return appRoot;
};

const showMaintenanceStub = rootEl => {
    getAppRoot(rootEl).render(<MaintenanceStub/>);
};

const renderApp = rootEl => {
    MaintenanceModeService.watch(() => showMaintenanceStub(rootEl));

    if (MaintenanceModeService.isActive()) {
        showMaintenanceStub(rootEl);
        MaintenanceModeService.connect();

        return;
    }

    initializeApp(rootEl);
};

const initializeApp = rootEl => {
    if (isGuest() || userSignedIn() && userStore.user.country_code === 'xx') {
        const flag = configStore.REG_FLAGS.find(x => x.code === window.CURRENT_IP_COUNTRY);

        if (flag) {
            userStore.user.current_country = flag.code;
            userStore.setSignUpCountry(flag.code);
        }
    }

    const language = getLanguageStorage();

    if (language !== configStore.CURRENT_LOCALE) {
        setLanguage(language);
    }

    amplitudeInit();
    init();
    articlesStore.initPromoNew();

    const recaptchaKey = getRecaptchaV3SiteKey();

    getAppRoot(rootEl).render(
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
            <Provider {...stores}>
                <Fragment>
                    <RouterProvider router={router} />
                </Fragment>
            </Provider>
        </GoogleReCaptchaProvider>
    );

    PwaInstallNotification.inform();
};
